import type { LocationData } from "@aws-amplify/ui-react-storage/browser";

import { AppRole } from "@/auth/roles";
import {
  EVIDO_INTERNAL_FOLDER_NAME,
  FOR_APPROVAL_FOLDER_NAME,
  MISCELLANEOUS_FOLDER_NAME,
  PROJECT_CONFIGURATION_FOLDER_NAME,
  RESULTS_FOLDER_NAME,
  SHARED_BY_EVIDO_FOLDER_NAME,
  UPLOAD_FOLDER_NAME,
  segmentsBelowProject,
} from "../folderNames";
import type { UploadRules } from "../upload/validation";
import {
  ANALYSIS_INPUT_RULES,
  FOR_APPROVAL_RULES,
  SHARED_FILES_RULES,
} from "../upload/validation";

export interface LocationBreadcrumbSource {
  readonly current: LocationData | undefined;
  readonly path: string;
}

/** Decides a folder's upload rules and toolbar actions. */
export const FolderTypes = {
  Upload: "upload",
  ForApproval: "forApproval",
  Results: "results",
  SharedByEvido: "sharedByEvido",
  StaffDocuments: "staffDocuments",
  Other: "other",
} as const;

export type FolderTypes = (typeof FolderTypes)[keyof typeof FolderTypes];

type FolderPolicy = {
  readonly uploadRules: UploadRules;
  readonly staffUploaders: readonly AppRole[];
  readonly isStaffDeletable: boolean;
  readonly staffReviewers: readonly AppRole[];
};

const STAFF_ROLES = [AppRole.Analyst, AppRole.QualityControl] as const;

const STAFF_DOCUMENTS_POLICY: FolderPolicy = {
  uploadRules: SHARED_FILES_RULES,
  staffUploaders: STAFF_ROLES,
  isStaffDeletable: true,
  staffReviewers: [],
};

const STAFF_READ_ONLY_POLICY: FolderPolicy = {
  uploadRules: ANALYSIS_INPUT_RULES,
  staffUploaders: [],
  isStaffDeletable: false,
  staffReviewers: [],
};

const FOLDER_POLICIES: Record<FolderTypes, FolderPolicy> = {
  [FolderTypes.Upload]: STAFF_READ_ONLY_POLICY,
  [FolderTypes.Results]: STAFF_READ_ONLY_POLICY,
  [FolderTypes.Other]: STAFF_READ_ONLY_POLICY,
  [FolderTypes.SharedByEvido]: STAFF_DOCUMENTS_POLICY,
  [FolderTypes.StaffDocuments]: STAFF_DOCUMENTS_POLICY,
  [FolderTypes.ForApproval]: {
    uploadRules: FOR_APPROVAL_RULES,
    staffUploaders: [AppRole.Analyst],
    isStaffDeletable: true,
    staffReviewers: [AppRole.QualityControl],
  },
};

const STAFF_DOCUMENTS_FOLDER_NAMES: readonly string[] = [
  PROJECT_CONFIGURATION_FOLDER_NAME,
  MISCELLANEOUS_FOLDER_NAME,
];

export function splitSegments(prefixOrPath: string): string[] {
  return prefixOrPath.split("/").filter(Boolean);
}

function projectFolderSegments(location: LocationBreadcrumbSource): string[] {
  return segmentsBelowProject(
    `${location.current?.prefix ?? ""}/${location.path}`,
  );
}

function isStaffDocumentsSegments(projectFolders: readonly string[]): boolean {
  return (
    projectFolders[0] === EVIDO_INTERNAL_FOLDER_NAME &&
    STAFF_DOCUMENTS_FOLDER_NAMES.includes(projectFolders[1] ?? "")
  );
}

/** Staff and approval types cover every depth below the project folder; the rest only itself. */
export function folderType(location: LocationBreadcrumbSource): FolderTypes {
  const projectFolders = projectFolderSegments(location);
  const [projectFolder] = projectFolders;
  if (projectFolder === SHARED_BY_EVIDO_FOLDER_NAME) {
    return FolderTypes.SharedByEvido;
  }
  if (projectFolder === FOR_APPROVAL_FOLDER_NAME) {
    return FolderTypes.ForApproval;
  }
  if (isStaffDocumentsSegments(projectFolders)) {
    return FolderTypes.StaffDocuments;
  }
  if (projectFolders.length !== 1) {
    return FolderTypes.Other;
  }
  switch (projectFolder) {
    case RESULTS_FOLDER_NAME:
      return FolderTypes.Results;
    case UPLOAD_FOLDER_NAME:
      return FolderTypes.Upload;
    default:
      return FolderTypes.Other;
  }
}

function policyAt(location: LocationBreadcrumbSource): FolderPolicy {
  return FOLDER_POLICIES[folderType(location)];
}

export function uploadRulesFor(
  location: LocationBreadcrumbSource,
): UploadRules {
  return policyAt(location).uploadRules;
}

export function isStaffWritableFolder(
  location: LocationBreadcrumbSource,
  role: AppRole,
): boolean {
  return policyAt(location).staffUploaders.includes(role);
}

export function isDeleteAvailable(
  location: LocationBreadcrumbSource,
  role: AppRole,
): boolean {
  return role !== AppRole.Customer && policyAt(location).isStaffDeletable;
}

export function isReviewAvailable(
  location: LocationBreadcrumbSource,
  role: AppRole,
): boolean {
  return policyAt(location).staffReviewers.includes(role);
}
